import { useEffect, useMemo, useState } from "react";
import { Modal } from "src/components/DialogComponent";
import { ArrowDown, ArrowUp } from "src/components/ui-icons/card";
import { Comment1 } from "src/components/ui-icons/card/Comment1";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { fetchData } from "../../store/filtersData/actions";
import { selectModelData } from "../../store/filtersData/selector";
import PassAudit5SScreenMobile from "../../passAudit5S/PassAudit5SScreen.mobile";
import { stripPatronymic } from "../../utils/userName";
import { buildMy5SCardsFilters } from "./my5SCardsFetch";

export interface Audit5sItemMobile {
  id: number;
  documentId?: string;
  planDate: string;
  auditType: string;
  area: string;
  zone: string;
  auditor: { name: string; avatar?: string };
  coAuditor: { name: string; avatar?: string };
  comment?: string;
  isOverdue: boolean;
  rawData?: any;
}

type Audits5sMainPageProps = {
  id: string;
  openComponentId: string;
  setOpenComponentId: React.Dispatch<React.SetStateAction<string>>;
  email?: string;
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function getUserDisplayName(strapiUser: any, usersMap: any): string {
  if (!strapiUser) return '—';
  const email = strapiUser.email?.toLowerCase();
  if (email && usersMap[email]) {
    return usersMap[email].displayName || usersMap[email].fullName || '—';
  }
  if (strapiUser.DisplayName) {
    const pn = strapiUser.PersonalNumber ? ` (${strapiUser.PersonalNumber})` : '';
    return `${stripPatronymic(strapiUser.DisplayName)}${pn}`;
  }
  return strapiUser.username || strapiUser.email || '—';
}

function getUserAvatar(strapiUser: any, usersMap: any): string | undefined {
  if (!strapiUser) return undefined;
  const email = strapiUser.email?.toLowerCase();
  if (email && usersMap[email]) {
    return usersMap[email].avatar || undefined;
  }
  return undefined;
}

const renderAvatar = (avatar: string | undefined, name: string) => {
  const shortName = name.split(' ').filter(Boolean).map((w) => w[0]).join('').substring(0, 2).toUpperCase();
  if (avatar) {
    return <img src={avatar} alt={name} className="w-6 h-6 rounded-full object-cover shrink-0" />;
  }
  return (
    <div className="w-6 h-6 shrink-0 rounded-full bg-[#012169] flex items-center justify-center text-[10px] font-medium text-white">
      {shortName}
    </div>
  );
};

const Audits5sMainPage = ({ id, openComponentId, setOpenComponentId, email }: Audits5sMainPageProps) => {
  const dispatch = useAppDispatch();

  const cardsRaw = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-cards'));
  const users: any = useAppSelector(selectUsers);
  const currentUser: any = useAppSelector(selectCurrentUser);

  const currentEmail = (email || currentUser?.email || '').toLowerCase();
  const isOpen = openComponentId === id;
  const [openComment, setOpenComment] = useState<string | null>(null);
  const [passCard, setPassCard] = useState<any>(null);

  useEffect(() => {
    if (!currentEmail) return;
    dispatch(fetchData('mt-5-s-cards', buildMy5SCardsFilters(currentEmail), { page: 1, pageSize: 500 }));
  }, [dispatch, currentEmail]);

  const data: Audit5sItemMobile[] = useMemo(() => {
    const myCards = currentEmail
      ? cardsRaw.filter((card: any) => {
          if (card.isAudit) return false;
          const auditorEmail = card.Auditor?.email?.toLowerCase();
          const coAuditorEmail = card.CoAuditor?.email?.toLowerCase();
          return auditorEmail === currentEmail || coAuditorEmail === currentEmail;
        })
      : [];

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    return myCards.map((card: any) => ({
      id: card.id,
      documentId: card.documentId,
      planDate: formatDate(card.PlannedDate || card.DateAudit),
      auditType: card.TypeAudit || '',
      area: card.Area?.Title || card.Area?.name || '',
      zone: card.Zone || '',
      auditor: { name: getUserDisplayName(card.Auditor, users), avatar: getUserAvatar(card.Auditor, users) },
      coAuditor: { name: getUserDisplayName(card.CoAuditor, users), avatar: getUserAvatar(card.CoAuditor, users) },
      comment: card.Comment || undefined,
      isOverdue: !card.isAudit && !!(card.PlannedDate || card.DateAudit) && new Date(card.PlannedDate || card.DateAudit) < startOfToday,
      rawData: card,
    }));
  }, [cardsRaw, users, currentEmail]);

  return (
    <section className="mb-7.5 cursor-pointer w-full flex-col flex">
      <FlexRow
        onClick={() => setOpenComponentId(isOpen ? "" : id)}
        className="w-full flex justify-between items-center mb-5"
      >
        <h3 className="text-[#637381] flex items-center font-medium text-[14px]">
          5S Аудиты (запланированные) · {data.length}
        </h3>
        {!isOpen ? <ArrowDown /> : <ArrowUp />}
      </FlexRow>
      {isOpen && (
        <FlexCol className="w-full gap-5">
          <FlexCol className="relative pb-5 overflow-y-auto bg-white rounded-lg table-custom shadow-custom no-scrollbar max-h-[calc(100vh-265px)] min-h-[60px]">
            <FlexCol className="w-full divide-y grow">
              {data.length > 0 ? (
                data.map((item) => (
                  <FlexCol
                    className="w-full py-4 px-4"
                    key={item.id}
                    onClick={() => setPassCard(item.rawData)}
                  >
                    <FlexRow className="w-full items-start justify-between">
                      <span className="text-sm not-italic text-[#637381]">
                        Дата план:&nbsp;
                        <span className={item.isOverdue ? "text-[#BA0C2F] font-bold" : "text-[#637381]"}>
                          {item.planDate}
                        </span>
                      </span>
                      {item.comment && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenComment(item.comment || '');
                          }}
                          className="shrink-0"
                          title="Комментарий"
                        >
                          <Comment1 />
                        </button>
                      )}
                    </FlexRow>
                    <FlexCol className="break-words mt-[4px]">
                      <span className="block text-sm font-normal not-italic text-[#637381]">
                        {[item.auditType, item.area, item.zone].filter(Boolean).join('/')}
                      </span>
                    </FlexCol>
                    <FlexRow className="items-center gap-2 mt-[8px]">
                      {renderAvatar(item.auditor.avatar, item.auditor.name)}
                      <span className="text-sm font-normal text-[#637381]">{item.auditor.name}</span>
                    </FlexRow>
                    <FlexRow className="items-center gap-2 mt-[8px]">
                      {renderAvatar(item.coAuditor.avatar, item.coAuditor.name)}
                      <span className="text-sm font-normal text-[#637381]">{item.coAuditor.name}</span>
                    </FlexRow>
                  </FlexCol>
                ))
              ) : (
                <FlexCol className="items-center justify-center w-full mt-5 mb-5">
                  <p>Нет запланированных аудитов</p>
                </FlexCol>
              )}
            </FlexCol>
          </FlexCol>
        </FlexCol>
      )}

      <PassAudit5SScreenMobile
        open={!!passCard}
        card={passCard}
        onClose={() => {
          setPassCard(null);
          if (currentEmail) {
            dispatch(fetchData('mt-5-s-cards', buildMy5SCardsFilters(currentEmail), { page: 1, pageSize: 500 }));
          }
        }}
      />

      <Modal
        zIndex={80}
        open={!!openComment}
        toggleDialog={() => setOpenComment(null)}
        modalClassName="w-[85vw] mx-auto min-h-0 sm:max-w-[420px] !pt-6"
        hideXMarkIcon
        closeOnBackdrop
      >
        <div className="px-6 pb-4 grow text-sm text-black whitespace-pre-line">
          {openComment}
        </div>
        <div className="px-6">
          <button
            type="button"
            className="w-full py-3 rounded-md bg-darkblue text-white"
            onClick={() => setOpenComment(null)}
          >
            Закрыть
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default Audits5sMainPage;
